import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { uuidv7 as uuid } from "uuidv7";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import Modal, {
  ModalTitle,
  OutsideClickModes,
} from "src/app/components/Modal/Modal";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { translateMessage } from "src/app/methods/translateMessage";
import {
  createDictionaryElements,
  setShouldCloseAddDictionaryElementModal,
} from "src/redux";
import { RootState } from "src/redux/reducers";
import SingleMetaField from "src/app/Task/components/TaskMetaFields/SingleMetaField";
import { tableDataType } from "src/app/components/Table/Table";
import isMetaFieldValueEmpty from "src/utils/isMetaFieldValueEmpty";
import { TaskMetaFieldName } from "src/app/Task/components/TaskMetaFields/TaskMetaFieldName";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import CustomInput from "src/app/components/CustomInput/CustomInput";
import { DictionaryField, TimeoutId } from "src/types";
import { ReactComponent as Asterisk } from "src/images/asterisk.svg";

import "./AddNewDictionaryElementModal.scss";

type DictionaryFieldWithOptionalValue = DictionaryField & {
  value?: string | string[];
};

interface AddNewDictionaryElementModalProps {
  onClose: () => void;
  dictionaryUuid: string;
  setElementToScrollUuid?: Dispatch<SetStateAction<string | null>>;
  getLists?: () => Promise<void>;
  fields: DictionaryFieldWithOptionalValue[];
  isLoading?: boolean;
}

export default function AddNewDictionaryElementModal({
  onClose,
  dictionaryUuid,
  setElementToScrollUuid,
  getLists,
  fields,
  isLoading,
}: AddNewDictionaryElementModalProps) {
  const intl = useIntl();
  const [name, setName] = useState<string>("");
  const [requiredFields, setRequiredFields] = useState<
    DictionaryFieldWithOptionalValue[]
  >([]);
  const [isAddingDictionaryElement, setIsAddingDictionaryElement] =
    useState(false);

  const dispatch = useDispatch();
  const {
    mainReducer: { activeWorkspaceUuid },
    dictionaryReducer: { dictionary, shouldCloseAddDictionaryElementModal },
  } = useSelector((state: RootState) => state);

  const { hasDynamicTitle } = dictionary || {};

  const requiredFieldsWithValues = requiredFields.filter(
    (field) => !isMetaFieldValueEmpty(field.value),
  );
  const hasAnyRequiredFields = requiredFields.length > 0;
  const doesAllRequiredFieldsHaveValues =
    requiredFieldsWithValues.length === requiredFields.length;

  const hasAnyUniqueFields = requiredFields.some((field) => field.unique);

  const handleConfirm = async () => {
    setIsAddingDictionaryElement(true);

    const elementUuid = uuid();
    if (setElementToScrollUuid) {
      setElementToScrollUuid(elementUuid);
    }

    const fieldsData = requiredFields.map((field) => ({
      wsDictionaryMetaFieldUuid: field.uuid,
      value: field.value,
    }));

    const elementsData = [
      {
        ...(!hasDynamicTitle && { name: name.trim() }),
        uuid: elementUuid,
        defaultMetaFieldsValues: fieldsData,
      },
    ];

    await dispatch(
      createDictionaryElements(
        activeWorkspaceUuid,
        dictionaryUuid,
        elementsData,
      ),
    );

    setIsAddingDictionaryElement(false);

    if (getLists) {
      getLists();
    }
  };

  const handleUpdateRequiredField = (
    newValue: string | string[],
    fieldUuid: string,
  ) => {
    setRequiredFields((prev) => {
      const updatedRequiredFieldsUserValues = [...prev];
      const indexOfExistingRequiredField = prev.findIndex(
        (item) => item.uuid === fieldUuid,
      );

      if (indexOfExistingRequiredField !== -1) {
        updatedRequiredFieldsUserValues[indexOfExistingRequiredField] = {
          ...updatedRequiredFieldsUserValues[indexOfExistingRequiredField],
          value: newValue,
        };
      }
      return updatedRequiredFieldsUserValues;
    });
  };

  useEffect(() => {
    if (shouldCloseAddDictionaryElementModal) {
      dispatch(setShouldCloseAddDictionaryElementModal(false));
      onClose();
    }
  }, [dispatch, onClose, shouldCloseAddDictionaryElementModal]);

  useEffect(() => {
    if (fields.length === 0) return;

    const filteredRequiredFields = fields.filter(
      (field) => field.required && !field.system,
    );
    setRequiredFields(filteredRequiredFields);
  }, [fields]);

  useEffect(() => {
    const textarea = document.querySelector(
      ".react-autosuggest__container .ws-textarea",
    ) as HTMLElement;

    let timeoutId: TimeoutId | undefined;

    if (textarea) {
      timeoutId = setTimeout(() => {
        textarea.click();
      }, 150);
    }

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <Modal
      className="add-new-dictionary-element-modal"
      onClose={onClose}
      onConfirmClick={handleConfirm}
      isLoading={isLoading}
      confirmButtonLoading={isAddingDictionaryElement}
      confirmButtonDisabled={
        (!hasDynamicTitle && name.length === 0) ||
        (hasAnyRequiredFields && !doesAllRequiredFieldsHaveValues)
      }
      closeButtonText={translateMessage({
        intl,
        id: "ws_cancel",
        defaultMessage: "Cancel",
      })}
      confirmButtonText={translateMessage({
        intl,
        id: "ws_confirm",
        defaultMessage: "Confirm",
      })}
      displayCancelButton
      outsideClick={OutsideClickModes.Warn}
    >
      <ModalTitle>
        <div>
          <IDHFormattedMessage id="ws_add" defaultMessage="Add" />{" "}
          {dictionary?.elementName}s
        </div>
      </ModalTitle>

      {!hasDynamicTitle && (
        <CustomInput
          id="wsDictionaryElementName"
          name="wsDictionaryElementName"
          className="add-new-dictionary-element-modal__input"
          onChange={(e) => setName(e.target.value)}
          placeholder={`${translateMessage({
            intl,
            id: "ws_enter",
            defaultMessage: "Enter",
          })} ${dictionary?.elementName}s ${translateMessage({
            intl,
            id: "ws_name",
            defaultMessage: "name",
          })}`}
        />
      )}

      {hasAnyRequiredFields && (
        <div className="add-new-dictionary-element-modal__required-fields">
          <div className="add-new-dictionary-element-modal__info-wrapper">
            <div className="text-h6-medium">
              <IDHFormattedMessage
                id="ws_required_fields"
                defaultMessage="Required fields"
              />
            </div>
            {hasAnyUniqueFields && (
              <div className="text-body-regular add-new-dictionary-element-modal__unique-field-info">
                <Asterisk />
                <IDHFormattedMessage
                  id="ws_field_must_contain_a_unique_value"
                  defaultMessage="Field must contain a unique value"
                />
              </div>
            )}
          </div>
          <div className="bottom-separator" />
          <div className="add-new-dictionary-element-modal__required-fields-list">
            {requiredFields.map((field) => (
              <div
                key={field.uuid}
                className={classNames(
                  "task-meta-fields__row",
                  (field.type === MetaFieldType.MultiSelect ||
                    MetaFieldType.GlobalCreator) &&
                    "task-meta-fields__row--auto",
                )}
              >
                <TaskMetaFieldName field={field} unique={field.unique} />
                <SingleMetaField
                  metaData={field}
                  dataType={tableDataType.DictionaryRequiredFields}
                  handleUpdateMetaValue={handleUpdateRequiredField}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}
